'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Select } from 'foundations-components/transitional/components/Select';
import { Map as ImmutableMap, List } from 'immutable';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { EQ } from 'reporting-data/constants/operators';
import { getDataType } from 'reporting-data/report/configReportGetters';
import { isConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { getUsageTracker } from 'reporting-ui-components/monitoring/usageTracker';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import BaseSelectEditorComponent from '../common/BaseSelectEditorComponent';
import { convertListToSingleValue, dataTypeSupportsAnalyticsViews, dataTypeSupportsSubdomains, getAnalyticsViewFilters, getSingleFilterValue, hasAnalyticsViewsAccess, viewIsSubdomain } from './analyticsViewUtils';
import useFetchAnalyticsViewOptions from './useFetchAnalyticsViewOptions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SETTINGS_URL = `/settings/${PortalIdParser.get()}/analytics-and-tracking/analytics-views`;
export const strategy = {
  getCurrentValue: report => {
    const matchedFilters = getAnalyticsViewFilters(report);
    return getSingleFilterValue(matchedFilters.get('analyticsView')) || getSingleFilterValue(matchedFilters.get('subdomain'));
  },
  updateReport: (report, newView) => {
    // reports can still have an IN filter where value is a list -- we should make analytics view only support EQ eventually
    const normalizedNewView = convertListToSingleValue(newView);
    if (!normalizedNewView) {
      return report.updateIn(['config', 'filters', 'custom'], List(), customFilters => customFilters.filter(filter => !['filterId', 'subdomain'].includes(filter.get('property'))));
    }
    const [filterPropertyToAdd, filterPropertyToRemove] = viewIsSubdomain(normalizedNewView) ? ['subdomain', 'filterId'] : ['filterId', 'subdomain'];
    let filterPropertyExists = false;
    const reportWithUpdatedFilters = report.updateIn(['config', 'filters', 'custom'], List(), customFilters => {
      const updatedExistingFilters = customFilters.reduce((newCustomFilters, filter) => {
        if (filter.get('property') === filterPropertyToAdd) {
          filterPropertyExists = true;
          return newCustomFilters.push(filter.set('value', normalizedNewView));
        }
        if (filter.get('property') === filterPropertyToRemove) {
          return newCustomFilters;
        }
        return newCustomFilters.push(filter);
      }, List());
      return filterPropertyExists ? updatedExistingFilters : updatedExistingFilters.push(ImmutableMap({
        property: filterPropertyToAdd,
        operator: EQ,
        value: normalizedNewView
      }));
    });
    return reportWithUpdatedFilters;
  }
};
const UnifiedAnalyticsViewFilterEditor = ({
  report,
  onChange,
  isV2FilterExperience,
  trackingAssetType,
  trackingApplicationName,
  trackingScreenName
}) => {
  const usageTracker = getUsageTracker();
  const includeSubdomains = dataTypeSupportsSubdomains(getDataType(report));
  const analyticsViews = useFetchAnalyticsViewOptions(includeSubdomains);
  const analyticsViewValue = strategy.getCurrentValue(report);
  const label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "reporting-enablement.reportEditors.analyticsViewEditor.title"
  });
  const dropdownFooter = /*#__PURE__*/_jsx(Link, {
    href: SETTINGS_URL,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-enablement.reportEditors.analyticsViewEditor.settings"
    })
  });
  const handleOnChange = useCallback(({
    target: {
      value: newView
    }
  }) => {
    onChange(strategy.updateReport(report, newView));
    usageTracker.track('changeSrvDateFilters', {
      action: DateActionTypes.UNIFIED_ANALYTICS_VIEW,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
  }, [onChange, report, usageTracker, trackingAssetType, trackingApplicationName, trackingScreenName, isV2FilterExperience]);
  if (isV2FilterExperience) {
    return analyticsViews ? /*#__PURE__*/_jsx(BaseSelectEditorComponent, {
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-enablement.reportEditors.analyticsViewEditor.optionGroups.all"
      }),
      tooltipHeader: label,
      onChange: handleOnChange,
      value: analyticsViewValue,
      options: analyticsViews,
      dropdownFooter: dropdownFooter
    }) : /*#__PURE__*/_jsx(_Fragment, {});
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: label,
    children: analyticsViews ? /*#__PURE__*/_jsx(Select, {
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-enablement.reportEditors.analyticsViewEditor.optionGroups.all"
      }),
      value: analyticsViewValue,
      options: analyticsViews,
      onChange: handleOnChange,
      dropdownFooter: dropdownFooter
    }) : /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "xs"
    })
  });
};
UnifiedAnalyticsViewFilterEditor.isUsableForReport = (report, __gates, scopes) => {
  return isConfigReport(report) && dataTypeSupportsAnalyticsViews(getDataType(report)) && hasAnalyticsViewsAccess({
    scopes
  });
};
UnifiedAnalyticsViewFilterEditor.propTypes = {
  report: ReportType.isRequired,
  onChange: PropTypes.func.isRequired,
  isV2FilterExperience: PropTypes.bool,
  trackingAssetType: PropTypes.string,
  trackingApplicationName: PropTypes.string,
  trackingScreenName: PropTypes.string
};
UnifiedAnalyticsViewFilterEditor.isFilterEditor = true;
export default UnifiedAnalyticsViewFilterEditor;